"use client";

import React, { useState, useMemo } from "react";
import {
  Cpu,
  Zap,
  DollarSign,
  Layers,
  Bot,
  AlertTriangle,
  CheckCircle2,
  SlidersHorizontal,
  Clock,
  Sparkles,
  Copy,
  Check,
  FileText,
  Code2,
  Database,
  Radio,
  ArrowRight,
  Shield,
  Gauge,
  TrendingDown,
} from "lucide-react";
import { CodeHighlight } from "./CodeHighlight";

export type TaskCategory =
  | "json_extraction"
  | "reasoning_code"
  | "tabular_scoring"
  | "multimodal_live"
  | "local_edge";

export type LatencyPreference = "ultra_low" | "interactive" | "async_batch";
export type TokenSizeOption = "compact" | "standard" | "large";

interface PresetConfig {
  id: string;
  name: string;
  icon: string;
  description: string;
  taskType: TaskCategory;
  latencyBudget: LatencyPreference;
  monthlyRequests: number;
  tokenSize: TokenSizeOption;
}

const PRESETS_DE: PresetConfig[] = [
  {
    id: "invoices",
    name: "Belege & Rechnungen",
    icon: "📄",
    description: "100k monatliche Dokumente als strukturierte JSON-Objekte extrahieren.",
    taskType: "json_extraction",
    latencyBudget: "interactive",
    monthlyRequests: 100000,
    tokenSize: "standard",
  },
  {
    id: "fraud",
    name: "Betrugserkennung & Scoring",
    icon: "🛡️",
    description: "Transaktionen in unter 15 Millisekunden risikobewerten.",
    taskType: "tabular_scoring",
    latencyBudget: "ultra_low",
    monthlyRequests: 500000,
    tokenSize: "compact",
  },
  {
    id: "code_audit",
    name: "Code-Audit & Deep Logic",
    icon: "🧠",
    description: "Komplexe Software-Architekturen & Sicherheitsanalysen mit Denkketten.",
    taskType: "reasoning_code",
    latencyBudget: "async_batch",
    monthlyRequests: 10000,
    tokenSize: "large",
  },
  {
    id: "offline_edge",
    name: "DSGVO-First Browser Edge",
    icon: "🔒",
    description: "Sensible Kundendaten ohne Cloud-API lokal auf dem Client verarbeiten.",
    taskType: "local_edge",
    latencyBudget: "ultra_low",
    monthlyRequests: 75000,
    tokenSize: "compact",
  },
];

const PRESETS_EN: PresetConfig[] = [
  {
    id: "invoices",
    name: "Invoices & Receipts",
    icon: "📄",
    description: "Extract 100k monthly documents as structured JSON objects.",
    taskType: "json_extraction",
    latencyBudget: "interactive",
    monthlyRequests: 100000,
    tokenSize: "standard",
  },
  {
    id: "fraud",
    name: "Fraud Detection & Scoring",
    icon: "🛡️",
    description: "Risk-score transactions in under 15 milliseconds.",
    taskType: "tabular_scoring",
    latencyBudget: "ultra_low",
    monthlyRequests: 500000,
    tokenSize: "compact",
  },
  {
    id: "code_audit",
    name: "Code Audit & Deep Logic",
    icon: "🧠",
    description: "Complex architectures and security audits with extended reasoning.",
    taskType: "reasoning_code",
    latencyBudget: "async_batch",
    monthlyRequests: 10000,
    tokenSize: "large",
  },
  {
    id: "offline_edge",
    name: "Privacy-First Browser Edge",
    icon: "🔒",
    description: "Process sensitive user data locally on the client without cloud APIs.",
    taskType: "local_edge",
    latencyBudget: "ultra_low",
    monthlyRequests: 75000,
    tokenSize: "compact",
  },
];

const TOKEN_SIZES_DE: Record<TokenSizeOption, { label: string; inTokens: number; outTokens: number }> = {
  compact: { label: "Kompakt (~300 In / 100 Out Tokens)", inTokens: 300, outTokens: 100 },
  standard: { label: "Standard (~1.200 In / 400 Out Tokens)", inTokens: 1200, outTokens: 400 },
  large: { label: "Großkontext (~7.000 In / 1.500 Out Tokens)", inTokens: 7000, outTokens: 1500 },
};

const TOKEN_SIZES_EN: Record<TokenSizeOption, { label: string; inTokens: number; outTokens: number }> = {
  compact: { label: "Compact (~300 In / 100 Out Tokens)", inTokens: 300, outTokens: 100 },
  standard: { label: "Standard (~1,200 In / 400 Out Tokens)", inTokens: 1200, outTokens: 400 },
  large: { label: "Large Context (~7,000 In / 1,500 Out Tokens)", inTokens: 7000, outTokens: 1500 },
};

export interface AiLandscapePlaygroundProps {
  locale?: "de" | "en";
}

export default function AiLandscapePlayground({ locale = "de" }: AiLandscapePlaygroundProps = {}) {
  const isEn = locale === "en";
  const presets = isEn ? PRESETS_EN : PRESETS_DE;
  const tokenSizes = isEn ? TOKEN_SIZES_EN : TOKEN_SIZES_DE;

  const [taskType, setTaskType] = useState<TaskCategory>("json_extraction");
  const [latencyBudget, setLatencyBudget] = useState<LatencyPreference>("interactive");
  const [monthlyRequests, setMonthlyRequests] = useState<number>(100000);
  const [tokenSize, setTokenSize] = useState<TokenSizeOption>("standard");
  const [activeCodeTab, setActiveCodeTab] = useState<"router" | "schema" | "benchmark">("router");
  const [copiedCode, setCopiedCode] = useState(false);

  // Active preset detection
  const activePresetId = presets.find(
    (p) =>
      p.taskType === taskType &&
      p.latencyBudget === latencyBudget &&
      p.monthlyRequests === monthlyRequests &&
      p.tokenSize === tokenSize,
  )?.id;

  const applyPreset = (preset: PresetConfig) => {
    setTaskType(preset.taskType);
    setLatencyBudget(preset.latencyBudget);
    setMonthlyRequests(preset.monthlyRequests);
    setTokenSize(preset.tokenSize);
  };

  // Calculation Engine based on 2026 production pricing
  const calculations = useMemo(() => {
    const { inTokens, outTokens } = tokenSizes[tokenSize];
    const totalInTokensM = (monthlyRequests * inTokens) / 1000000;
    const totalOutTokensM = (monthlyRequests * outTokens) / 1000000;

    // Baseline: Monolithic Frontier Model (e.g. GPT-6 Sol / Claude Opus 5.5 class: $3.00 in / $15.00 out per 1M)
    const naiveCost = totalInTokensM * 3.0 + totalOutTokensM * 15.0;
    const naiveLatencyMs =
      taskType === "reasoning_code" ? 11500 : tokenSize === "large" ? 4200 : 2300;
    const naiveHallucinationRisk = isEn
      ? taskType === "tabular_scoring"
        ? "Extremely High (95%)"
        : taskType === "json_extraction"
        ? "Moderate (12%)"
        : "Low (3%)"
      : taskType === "tabular_scoring"
      ? "Extrem hoch (95%)"
      : taskType === "json_extraction"
      ? "Moderat (12%)"
      : "Gering (3%)";

    // Optimized Stack Calculation
    let optimizedStackName = "";
    let optimizedBadge = "";
    let optimizedCost = 0;
    let optimizedLatencyMs = 0;
    let pipelineSteps: string[] = [];
    let architecturalInsight = "";
    let warningNote = "";
    let codeSnippets: { router: string; schema: string; benchmark: string };

    switch (taskType) {
      case "tabular_scoring":
        optimizedStackName = isEn
          ? "Classical Tabular ML (XGBoost 3.0 / LightGBM 4.2 on CPU)"
          : "Klassisches Tabular ML (XGBoost 3.0 / LightGBM 4.2 auf CPU)";
        optimizedBadge = isEn
          ? "100% Deterministic • $0 API Cost"
          : "100% Deterministisch • 0 $ API-Kosten";
        optimizedCost = 0; // Local / Server CPU execution
        optimizedLatencyMs = 4;
        pipelineSteps = isEn
          ? [
              "1. HTTP Payload Ingestion",
              "2. Feature Vector Normalization (< 1ms)",
              "3. XGBoost 3.0 Inference on Container CPU (3ms)",
              "4. Threshold Validation & Response",
            ]
          : [
              "1. HTTP Payload Empfang",
              "2. Feature Vector Normalisierung (< 1ms)",
              "3. XGBoost 3.0 Inferenz auf Container-CPU (3ms)",
              "4. Schwellenwert-Validierung & Response",
            ];
        architecturalInsight = isEn
          ? "For tabular fraud detection, churn prediction, or risk scoring, a generative LLM is a critical anti-pattern. XGBoost 3.0 provides mathematically verified probabilities in 4ms with zero token cost."
          : "Für tabellarische Betrugserkennung, Churn-Prediction oder numerische Risikobewertung ist ein generatives LLM ein gravierendes Anti-Pattern. XGBoost 3.0 liefert in 4ms mathematisch exakte Wahrscheinlichkeiten ohne Tokenkosten.";
        warningNote = isEn
          ? "Never deploy generative LLMs for tabular scoring: sluggish latency (> 2s), enormous token expenditure, and hallucinated calculations."
          : "Niemals LLMs für Tabellen-Scoring einsetzen: Hohe Latenz (> 2s), horrende Tokenkosten und erfundene Zahlen.";
        codeSnippets = {
          router: isEn
            ? `// Next.js API Route: Deterministic Tabular Scoring without LLM
import { NextResponse } from "next/server";
import { predictFraudScore } from "@/lib/ml/fraud-xgboost";

export async function POST(req: Request) {
  const transaction = await req.json();

  // 100% deterministic calculation in < 5ms on Server CPU
  const riskScore = await predictFraudScore({
    amount: transaction.amount,
    userAgeDays: transaction.userAgeDays,
    velocityHour: transaction.velocityHour,
  });

  return NextResponse.json({
    fraud: riskScore > 0.85,
    score: riskScore,
    model: "xgboost-v3.0-quantized",
    latencyMs: 4,
  });
}`
            : `// Next.js API Route: Deterministisches Tabular Scoring ohne LLM
import { NextResponse } from "next/server";
import { predictFraudScore } from "@/lib/ml/fraud-xgboost";

export async function POST(req: Request) {
  const transaction = await req.json();

  // 100% deterministische Berechnung in < 5ms auf Server-CPU
  const riskScore = await predictFraudScore({
    amount: transaction.amount,
    userAgeDays: transaction.userAgeDays,
    velocityHour: transaction.velocityHour,
  });

  return NextResponse.json({
    fraud: riskScore > 0.85,
    score: riskScore,
    model: "xgboost-v3.0-quantized",
    latencyMs: 4,
  });
}`,
          schema: isEn
            ? `// Zod Schema for numerical feature vectorization
import { z } from "zod";

export const TransactionFeatureSchema = z.object({
  amount: z.number().positive(),
  userAgeDays: z.number().int().nonnegative(),
  velocityHour: z.number().int().min(0).max(100),
  deviceTrustScore: z.number().min(0).max(1),
});`
            : `// Zod-Schema für numerische Vektorisierung
import { z } from "zod";

export const TransactionFeatureSchema = z.object({
  amount: z.number().positive(),
  userAgeDays: z.number().int().nonnegative(),
  velocityHour: z.number().int().min(0).max(100),
  deviceTrustScore: z.number().min(0).max(1),
});`,
          benchmark: isEn
            ? `// Benchmark: XGBoost 3.0 vs. Frontier Flagship (GPT-6 Sol)
Metric              XGBoost 3.0 CPU    Frontier LLM (GPT-6 Sol)
------------------------------------------------------------
Inference Latency:  4 ms               2,300 ms (575x slower)
Monthly API Cost:   $0.00 API          $${naiveCost.toFixed(2)}
Hallucination:      0% (Math)          95% (unreliable)
Throughput / Core:  12,000 Req/sec     < 15 Req/sec`
            : `// Benchmark: XGBoost 3.0 vs. Frontier Flaggschiff (GPT-6 Sol)
Metric              XGBoost 3.0 CPU    Frontier LLM (GPT-6 Sol)
------------------------------------------------------------
Inferenz-Latenz:    4 ms               2.300 ms (575x langsamer)
Monatliche Kosten:  0,00 $ API         ${naiveCost.toFixed(2)} $
Halluzination:      0% (Mathematik)    95% (unzuverlässig)
Durchsatz / Core:   12.000 Req/sec     < 15 Req/sec`,
        };
        break;

      case "local_edge":
        optimizedStackName = isEn
          ? "Local On-Device SLM (Gemma 3 2B / Phi-4 Mini via WebNN)"
          : "Lokales On-Device SLM (Gemma 3 2B / Phi-4 Mini via WebNN)";
        optimizedBadge = isEn
          ? "Zero Cloud Cost • 100% Privacy"
          : "Zero-Cloud-Cost • 100% DSGVO-Privatsphäre";
        optimizedCost = 0; // Client-side inference
        optimizedLatencyMs = 45;
        pipelineSteps = isEn
          ? [
              "1. User input stays in client browser memory",
              "2. WebNN NPU/GPU hardware acceleration",
              "3. Gemma 3 2B / Phi-4 Mini local inference",
              "4. Instant UI update with zero server roundtrip",
            ]
          : [
              "1. Eingabe verbleibt im Browser-Memory",
              "2. WebNN NPU/GPU Hardware-Beschleunigung",
              "3. Gemma 3 2B / Phi-4 Mini Inferenz auf Client",
              "4. Sofortige UI-Reaktion ohne Server-Roundtrip",
            ];
        architecturalInsight = isEn
          ? "Inference directly on client hardware eliminates server costs entirely, functions offline, and complies with stringent data protection standards with zero external cloud calls."
          : "Inferenz direkt auf der Client-Hardware eliminiert Serverkosten vollständig, funktioniert offline und erfüllt strengste Datenschutz-Vorgaben ohne externe Cloud-Calls.";
        warningNote = isEn
          ? "Requires modern devices with NPU or WebGPU. Provide fallback to cloud worker for legacy browsers."
          : "Setzt moderne Endgeräte mit NPU oder WebGPU voraus. Bei älteren Browsern Fallback auf Cloud-Worker vorsehen.";
        codeSnippets = {
          router: isEn
            ? `// Client-Side WebNN Inference: 100% local without server call
"use client";
import * as ort from "onnxruntime-web/webgpu";

export async function runLocalExtraction(userPrompt: string) {
  // Gemma 3 2B or Phi-4 Mini 4-bit quantized
  const session = await ort.InferenceSession.create("/models/gemma-3-2b-q4.onnx", {
    executionProviders: ["webnn", "webgpu"],
  });

  const feeds = prepareTokens(userPrompt);
  const results = await session.run(feeds);
  
  // $0 API cost, 0 ms network latency
  return decodeTokens(results.output);
}`
            : `// Client-Side WebNN Inferenz: 100% lokal ohne Server-Call
"use client";
import * as ort from "onnxruntime-web/webgpu";

export async function runLocalExtraction(userPrompt: string) {
  // Gemma 3 2B oder Phi-4 Mini 4-bit quantisiert
  const session = await ort.InferenceSession.create("/models/gemma-3-2b-q4.onnx", {
    executionProviders: ["webnn", "webgpu"],
  });

  const feeds = prepareTokens(userPrompt);
  const results = await session.run(feeds);
  
  // 0$ API Kosten, 0 ms Netzwerk-Latenz
  return decodeTokens(results.output);
}`,
          schema: isEn
            ? `// Hardware Feature Detection before WebNN initialization
export async function checkNpuSupport(): Promise<boolean> {
  if (typeof navigator !== "undefined" && "ml" in navigator) {
    const context = await (navigator as any).ml.createContext({
      deviceType: "npu", // Intel Core Ultra, Apple Neural Engine M3/M4, Snapdragon X
    });
    return !!context;
  }
  return false;
}`
            : `// Hardware Feature-Detection vor WebNN-Start
export async function checkNpuSupport(): Promise<boolean> {
  if (typeof navigator !== "undefined" && "ml" in navigator) {
    const context = await (navigator as any).ml.createContext({
      deviceType: "npu", // Intel Core Ultra, Apple Neural Engine M3/M4, Qualcomm Snapdragon X
    });
    return !!context;
  }
  return false;
}`,
          benchmark: isEn
            ? `// Benchmark: Browser WebNN vs. Cloud API
Metric              WebNN Client (Gemma 3) Cloud API (Gemini 3.8 Flash)
------------------------------------------------------------
Network Latency:    0 ms (Offline)         150 - 320 ms
API Cost:           $0.00 (perpetual)      Per-token billing
Data Privacy:       Zero egress            Subject to cloud policy
Hardware Load:      Client NPU / GPU       Provider Datacenter`
            : `// Benchmark: Browser WebNN vs. Cloud API
Metric              WebNN Client (Gemma 3) Cloud API (Gemini 3.8 Flash)
------------------------------------------------------------
Netzwerk-Latenz:    0 ms (Offline)         150 - 320 ms
API-Kosten:         0,00 $ (immer)         Token-Abrechnung
Datenschutz:        Kein Datenabfluss      Cloud-Provider-Policy
Hardware-Last:      Client NPU / GPU       Provider Datacenter`,
        };
        break;

      case "json_extraction":
        optimizedStackName = isEn
          ? "Compound System: Worker SLM (Gemini 3.8 Flash / Haiku 4.5) + Zod Schema"
          : "Compound System: Worker-SLM (Gemini 3.8 Flash / Haiku 4.5) + Zod-Schema";
        optimizedBadge = isEn
          ? "High-Throughput • 98% Cost Reduction"
          : "High-Throughput • 98% Kostenreduktion";
        // 2026 Worker pricing: $0.08 in / $0.30 out per 1M tokens
        optimizedCost = totalInTokensM * 0.08 + totalOutTokensM * 0.3;
        optimizedLatencyMs = 210;
        pipelineSteps = isEn
          ? [
              "1. Request Ingestion & Cache Lookup (< 10ms)",
              "2. Inference with Worker Model (Gemini 3.8 Flash / Claude Haiku 4.5)",
              "3. Native JSON Schema Enforcement (response_schema)",
              "4. Zod Validation & Guaranteed Return (< 220ms)",
            ]
          : [
              "1. Request Empfang & Cache-Lookup (< 10ms)",
              "2. Inferenz mit Worker-Modell (Gemini 3.8 Flash / Claude Haiku 4.5)",
              "3. Native JSON-Schema-Erzwingung (response_schema)",
              "4. Zod-Validierung & garantierter Return (< 220ms)",
            ];
        architecturalInsight = isEn
          ? "For structured extraction, fast worker models (Gemini 3.8 Flash or Claude Haiku 4.5) combined with strict JSON schema match 25x more expensive frontier flagships in accuracy – at near-instant latencies of ~210ms."
          : "Für strukturierte Informationsextraktion sind schnelle Worker-Modelle (wie Gemini 3.8 Flash oder Claude Haiku 4.5) mit striktem JSON-Schema den 25-mal teureren Frontier-Flaggschiffen qualitativ ebenbürtig – bei kaum spürbarer Latenz von ~210ms.";
        warningNote = isEn
          ? "Always enforce native schema (`response_format` / `response_schema`) to prevent unparsed conversational fluff."
          : "Immer natives Schema (`response_format` / `response_schema`) nutzen, um Freitext-Müll vorzubeugen.";
        codeSnippets = {
          router: isEn
            ? `// Next.js API Route: Lightweight Worker with strict Schema Enforcement
import { GoogleGenAI, Type } from "@google/genai";
import { InvoiceSchema } from "@/lib/schemas/invoice";

const ai = new GoogleGenAI({});

export async function POST(req: Request) {
  const { documentText } = await req.json();

  // Gemini 3.8 Flash: $0.08 / 1M Input Tokens, ~210ms TTFT latency
  const response = await ai.models.generateContent({
    model: "gemini-3.8-flash",
    contents: documentText,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          invoiceNumber: { type: Type.STRING },
          totalAmount: { type: Type.NUMBER },
          taxAmount: { type: Type.NUMBER },
          items: {
            type: Type.ARRAY,
            items: { type: Type.STRING },
          },
        },
        required: ["invoiceNumber", "totalAmount"],
      },
    },
  });

  const parsed = InvoiceSchema.parse(JSON.parse(response.text!));
  return Response.json(parsed);
}`
            : `// Next.js API Route: Schlanker Worker mit Schema-Zwang
import { GoogleGenAI, Type } from "@google/genai";
import { InvoiceSchema } from "@/lib/schemas/invoice";

const ai = new GoogleGenAI({});

export async function POST(req: Request) {
  const { documentText } = await req.json();

  // Gemini 3.8 Flash: $0.08 / 1M Input Tokens, ~210ms TTFT Latenz
  const response = await ai.models.generateContent({
    model: "gemini-3.8-flash",
    contents: documentText,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          invoiceNumber: { type: Type.STRING },
          totalAmount: { type: Type.NUMBER },
          taxAmount: { type: Type.NUMBER },
          items: {
            type: Type.ARRAY,
            items: { type: Type.STRING },
          },
        },
        required: ["invoiceNumber", "totalAmount"],
      },
    },
  });

  const parsed = InvoiceSchema.parse(JSON.parse(response.text!));
  return Response.json(parsed);
}`,
          schema: isEn
            ? `// Zod Schema for runtime payload verification
import { z } from "zod";

export const InvoiceSchema = z.object({
  invoiceNumber: z.string().min(1),
  totalAmount: z.number().positive(),
  taxAmount: z.number().nonnegative().optional(),
  items: z.array(z.string()).default([]),
});`
            : `// Zod-Schema zur Laufzeit-Verifikation
import { z } from "zod";

export const InvoiceSchema = z.object({
  invoiceNumber: z.string().min(1),
  totalAmount: z.number().positive(),
  taxAmount: z.number().nonnegative().optional(),
  items: z.array(z.string()).default([]),
});`,
          benchmark: isEn
            ? `// Benchmark: Gemini 3.8 Flash vs. GPT-6 Sol Monolith
Metric              Gemini 3.8 Flash   GPT-6 Sol Monolith
------------------------------------------------------------
Cost / 1M In:       $0.08              $3.00 (37.5x costlier)
Cost / 1M Out:      $0.30              $15.00 (50x costlier)
Monthly Cost:       $${optimizedCost.toFixed(2)}             $${naiveCost.toFixed(2)}
Latency (TTFT):     210 ms             2,300 ms (11x slower)
JSON Validity:      99.9% (Schema)     97.5% (Prompt)`
            : `// Benchmark: Gemini 3.8 Flash vs. GPT-6 Sol Monolith
Metric              Gemini 3.8 Flash   GPT-6 Sol Monolith
------------------------------------------------------------
Kosten / 1M In:     0,08 $             3,00 $ (37,5x teurer!)
Kosten / 1M Out:    0,30 $             15,00 $ (50x teurer!)
Monatliche Kosten:  ${optimizedCost.toFixed(2)} $             ${naiveCost.toFixed(2)} $
Latenz (TTFT):      210 ms             2.300 ms (11x langsamer)
JSON-Validität:     99,9% (Schema)     97,5% (Prompt)`,
        };
        break;

      case "reasoning_code":
        optimizedStackName = isEn
          ? "Cascade Router: Worker Triage + Reasoning Escalation (Claude Fable 5 / DeepSeek-V4 Pro)"
          : "Kaskaden-Router: Worker-Triage + Reasoning-Escalation (Claude Fable 5 / DeepSeek-V4 Pro)";
        optimizedBadge = isEn
          ? "Reasoning Cascade • FrugalGPT Pattern"
          : "Reasoning-Kaskade • FrugalGPT-Pattern";
        // 80% handled by worker ($0.10/$0.35), 20% escalated to reasoning model ($1.50/$6.00)
        const workerCost = (totalInTokensM * 0.1 + totalOutTokensM * 0.35) * 0.8;
        const reasoningCost = (totalInTokensM * 1.5 + totalOutTokensM * 6.0) * 0.2;
        optimizedCost = workerCost + reasoningCost;
        optimizedLatencyMs = 950; // Average across 80% fast (250ms) + 20% deep thinking (3800ms)
        pipelineSteps = isEn
          ? [
              "1. Gateway Router checks code complexity & AST depth",
              "2. 80% standard refactorings -> Worker (Gemini 3.8 Flash / Haiku 4.5)",
              "3. 20% complex logic -> Reasoning model (Claude Fable 5 / DeepSeek-V4 Pro)",
              "4. Synthesis & automated linter / test verification",
            ]
          : [
              "1. Gateway Router prüft Komplexität & AST-Tiefe",
              "2. 80% Routinefragen -> Worker-Modell (Gemini 3.8 Flash / Haiku 4.5)",
              "3. 20% komplexe Logik -> Reasoning-Modell (Claude Fable 5 / DeepSeek-V4 Pro)",
              "4. Synthese & automatischer Linter/Test-Lauf",
            ];
        architecturalInsight = isEn
          ? "FrugalGPT routers cascade requests: 80% of routine refactorings are resolved by an affordable worker in 250ms. Only deep dependency chains trigger frontier reasoning (Claude Fable 5 or DeepSeek-V4 Pro) with extended thinking."
          : "FrugalGPT-Router kaskadieren Aufgaben: 80% Routine-Refactorings erledigt ein günstiger Worker in 250ms. Nur bei tiefen Abhängigkeiten wird das Flaggschiff (Claude Fable 5 oder DeepSeek-V4 Pro) mit Extended Thinking aktiviert.";
        warningNote = isEn
          ? "Avoid endless autonomous loops with reasoning models: enforce strict `max_thinking_tokens` caps."
          : "Autonome Endlosschleifen mit Reasoning-Modellen meiden: `max_thinking_tokens` strikt begrenzen.";
        codeSnippets = {
          router: isEn
            ? `// FrugalGPT Router: Dual-stage cascade for code audits
import { callWorkerModel } from "@/lib/ai/worker";
import { callReasoningModel } from "@/lib/ai/reasoning";

export async function auditCode(sourceCode: string) {
  // Stage 1: Rapid triage check via Worker (~200ms, $0.08/1M)
  const complexity = estimateComplexity(sourceCode);

  if (complexity < 0.7) {
    // 80% of queries: Fast Worker Model (Gemini 3.8 Flash / Haiku 4.5)
    return await callWorkerModel({
      model: "gemini-3.8-flash",
      prompt: "Refactor code according to standards",
      code: sourceCode,
    });
  }

  // Stage 2: Escalate to Claude Fable 5 / DeepSeek-V4 Pro only on complex ASTs
  return await callReasoningModel({
    model: "claude-fable-5",
    thinking: { type: "enabled", budget_tokens: 4096 },
    prompt: "Perform deep architectural analysis and formal security verification",
    code: sourceCode,
  });
}`
            : `// FrugalGPT Router: Zweistufige Kaskade für Code-Audits
import { callWorkerModel } from "@/lib/ai/worker";
import { callReasoningModel } from "@/lib/ai/reasoning";

export async function auditCode(sourceCode: string) {
  // Stufe 1: Schneller Triage-Check via Worker (~200ms, $0.08/1M)
  const complexity = estimateComplexity(sourceCode);

  if (complexity < 0.7) {
    // 80% aller Fälle: Günstiges Worker-Modell (Gemini 3.8 Flash / Haiku 4.5)
    return await callWorkerModel({
      model: "gemini-3.8-flash",
      prompt: "Refactor code according to standards",
      code: sourceCode,
    });
  }

  // Stufe 2: Eskalation an Claude Fable 5 / DeepSeek-V4 Pro nur bei komplexen ASTs
  return await callReasoningModel({
    model: "claude-fable-5",
    thinking: { type: "enabled", budget_tokens: 4096 },
    prompt: "Perform deep architectural analysis and formal security verification",
    code: sourceCode,
  });
}`,
          schema: isEn
            ? `// Configuration parameters for cascade routing decisions
export interface RouterConfig {
  workerModel: "gemini-3.8-flash" | "claude-haiku-4-5" | "deepseek-v4-1-flash";
  reasoningModel: "claude-fable-5" | "deepseek-v4-pro" | "gpt-6-sol";
  maxThinkingTokens: number; // e.g. 4096
  complexityThreshold: number; // 0.70
}`
            : `// Konfigurations-Parameter für Kaskaden-Entscheidungen
export interface RouterConfig {
  workerModel: "gemini-3.8-flash" | "claude-haiku-4-5" | "deepseek-v4-1-flash";
  reasoningModel: "claude-fable-5" | "deepseek-v4-pro" | "gpt-6-sol";
  maxThinkingTokens: number; // z.B. 4096
  complexityThreshold: number; // 0.70
}`,
          benchmark: isEn
            ? `// Benchmark: Cascade Router vs. Static Reasoning Flagship
Metric              Cascade Router     Always Claude Fable 5
------------------------------------------------------------
Workload Split:     80% Worker / 20% Fable  100% Claude Fable 5
Monthly Cost:       $${optimizedCost.toFixed(2)}             $${(totalInTokensM * 1.5 + totalOutTokensM * 6.0).toFixed(2)}
Average Latency:    950 ms             6,400 ms (6.7x slower)
Quality / Accuracy: Identical          Identical`
            : `// Benchmark: Kaskaden-Router vs. Permanentes Reasoning-Modell
Metric              Kaskaden-Router    Permanentes Claude Fable 5
------------------------------------------------------------
Kostenverteilung:   80% Worker / 20% Fable  100% Claude Fable 5
Monatliche Kosten:  ${optimizedCost.toFixed(2)} $             ${(totalInTokensM * 1.5 + totalOutTokensM * 6.0).toFixed(2)} $
Mittlere Latenz:    950 ms             6.400 ms (6,7x langsamer)
Qualität / Accuracy: Identisch          Identisch`,
        };
        break;

      case "multimodal_live":
        optimizedStackName = isEn
          ? "Native End-to-End Multimodal (Gemini 3.8 Flash Live / Cartesia Sonic 2)"
          : "Natives End-to-End Multimodal (Gemini 3.8 Flash Live / Cartesia Sonic 2)";
        optimizedBadge = isEn
          ? "Real-Time Audio • Sub-250ms Voice-to-Voice"
          : "Echtzeit-Audio • Sub-250ms Voice-to-Voice";
        optimizedCost = totalInTokensM * 0.35 + totalOutTokensM * 1.0;
        optimizedLatencyMs = 240;
        pipelineSteps = isEn
          ? [
              "1. Direct audio stream via bi-directional WebSocket",
              "2. Zero separate STT transcription (saves 600ms cascade delay)",
              "3. Native audio generation directly from model weights",
              "4. Fluid conversational cadence (< 250ms reaction time)",
            ]
          : [
              "1. Direkter Audio-Stream via Bi-Directional WebSocket",
              "2. Kein separates STT-Transkript (spart 600ms Kaskaden-Latenz)",
              "3. Native Audio-Synthese direkt aus Modell-Weights",
              "4. Flüssige Gesprächsinteraktion (< 250ms Reaktionszeit)",
            ];
        architecturalInsight = isEn
          ? "The legacy cascade (Speech-to-Text -> LLM text -> Text-to-Speech) adds 2 to 3 seconds of friction. Native omni models (such as Gemini 3.8 Flash Live) process streaming audio with natural dialogue dynamics below 250ms."
          : "Die alte Kaskade (Speech-to-Text -> LLM-Text -> Text-to-Speech) verursacht 2 bis 3 Sekunden Verzögerung. Moderne native Omni-Modelle (wie Gemini 3.8 Flash Live) verarbeiten Audio direkt im Stream bei natürlicher Gesprächsdynamik unter 250ms.";
        warningNote = isEn
          ? "Requires persistent WebSocket connection. For async batch audio, decoupled Whisper + LLM remains more cost-efficient."
          : "Erfordert stabile WebSocket-Verbindung. Für asynchrone Batch-Prozesse ist separates Whisper + LLM kostengünstiger.";
        codeSnippets = {
          router: isEn
            ? `// WebSocket Client: Native Voice-to-Voice Connection
import { GoogleGenAI } from "@google/genai";

export async function startLiveSession(audioStream: MediaStream) {
  const ai = new GoogleGenAI({});
  
  // Direct bidirectional real-time stream bypassing STT/TTS cascade
  const session = await ai.aio.live.connect({
    model: "gemini-3.8-flash",
    config: {
      generationConfig: { responseModalities: ["AUDIO"] },
      speechConfig: { voiceConfig: { prebuiltVoiceConfig: { voiceName: "Aoede" } } },
    },
  });

  // Continuously stream audio frames: latency < 250ms
  pipeAudioToSession(audioStream, session);
}`
            : `// WebSocket Client: Native Voice-to-Voice Verbindung
import { GoogleGenAI } from "@google/genai";

export async function startLiveSession(audioStream: MediaStream) {
  const ai = new GoogleGenAI({});
  
  // Direkter bidirektionaler Echtzeit-Kanal ohne STT/TTS-Kaskade
  const session = await ai.aio.live.connect({
    model: "gemini-3.8-flash",
    config: {
      generationConfig: { responseModalities: ["AUDIO"] },
      speechConfig: { voiceConfig: { prebuiltVoiceConfig: { voiceName: "Aoede" } } },
    },
  });

  // Audio-Frames kontinuierlich streamen: Latenz < 250ms
  pipeAudioToSession(audioStream, session);
}`,
          schema: isEn
            ? `// Live Session WebSocket configuration options
export interface LiveSessionOptions {
  sampleRateHz: 16000 | 24000;
  voice: "Puck" | "Charon" | "Kore" | "Fenrir" | "Aoede";
  enableVAD: boolean; // Voice Activity Detection
}`
            : `// Live-Session WebSocket-Konfiguration
export interface LiveSessionOptions {
  sampleRateHz: 16000 | 24000;
  voice: "Puck" | "Charon" | "Kore" | "Fenrir" | "Aoede";
  enableVAD: boolean; // Voice Activity Detection
}`,
          benchmark: isEn
            ? `// Benchmark: Native Omni Live vs. Legacy STT-LLM-TTS Cascade
Metric              Gemini 3.8 Flash Live Legacy Cascade (Whisper+LLM+TTS)
------------------------------------------------------------
Audio Latency:      220 - 250 ms          1,800 - 3,200 ms (unnatural)
Emotion / Nuance:   Preserved             Lost in textual intermediate step
API Complexity:     1 WebSocket           3 discrete API services`
            : `// Benchmark: Native Omni Live vs. Legacy STT-LLM-TTS Kaskade
Metrik              Gemini 3.8 Flash Live Legacy Kaskade (Whisper+LLM+TTS)
------------------------------------------------------------
Audio Latenz:       220 - 250 ms          1.800 - 3.200 ms (unnatürlich)
Emotion / Nuance:   Erhalten              Geht im Text-Zwischenschritt verloren
API-Komplexität:    1 WebSocket           3 separate API-Services`,
        };
        break;
    }

    const savingsDollars = Math.max(0, naiveCost - optimizedCost);
    const savingsPercent = naiveCost > 0 ? Math.round((savingsDollars / naiveCost) * 100) : 100;
    const speedupFactor = (naiveLatencyMs / Math.max(1, optimizedLatencyMs)).toFixed(1);

    return {
      naiveCost,
      naiveLatencyMs,
      naiveHallucinationRisk,
      optimizedStackName,
      optimizedBadge,
      optimizedCost,
      optimizedLatencyMs,
      savingsDollars,
      savingsPercent,
      speedupFactor,
      pipelineSteps,
      architecturalInsight,
      warningNote,
      codeSnippets,
    };
  }, [taskType, latencyBudget, monthlyRequests, tokenSize, tokenSizes, isEn]);

  const handleCopyCode = () => {
    const text = calculations.codeSnippets[activeCodeTab];
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const numberLocale = isEn ? "en-US" : "de-DE";

  return (
    <div className="flex w-full flex-col gap-8 select-none">
      {/* Top Banner: Presets */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
            <Sparkles className="h-4 w-4 text-purple-500" />
            {isEn ? "1-Click Real-World Presets" : "1-Klick Praxisszenarien (Presets)"}
          </span>
          <span className="text-xs text-neutral-400 dark:text-neutral-500">
            {isEn ? "Select a preset for rapid benchmarking" : "Wähle ein Profil zum schnellen Testen"}
          </span>
        </div>

        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
          {presets.map((preset) => {
            const isActive = activePresetId === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => applyPreset(preset)}
                className={`group flex min-h-[56px] items-center gap-3 rounded-xl border p-3 text-left transition-all duration-200 cursor-pointer ${
                  isActive
                    ? "border-purple-500/80 bg-purple-500/10 shadow-sm ring-1 ring-purple-500/30 dark:border-purple-400/80 dark:bg-purple-950/30"
                    : "border-neutral-200/90 bg-neutral-50/70 hover:border-neutral-300 hover:bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-900/50 dark:hover:border-neutral-700 dark:hover:bg-neutral-900"
                }`}
              >
                <span className="text-xl shrink-0 group-hover:scale-110 transition-transform">
                  {preset.icon}
                </span>
                <div className="flex flex-col min-w-0">
                  <span
                    className={`text-xs font-bold truncate ${
                      isActive
                        ? "text-purple-700 dark:text-purple-300"
                        : "text-neutral-900 dark:text-neutral-100"
                    }`}
                  >
                    {preset.name}
                  </span>
                  <span className="text-[11px] text-neutral-500 dark:text-neutral-400 line-clamp-1">
                    {preset.description}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Configuration Grid */}
      <div className="grid grid-cols-1 gap-6 rounded-2xl border border-neutral-200/90 bg-neutral-50/50 p-5 dark:border-neutral-800 dark:bg-neutral-900/40 lg:grid-cols-12">
        {/* Left Column: Task Type Selection (7 cols) */}
        <div className="flex flex-col gap-4 lg:col-span-7">
          <label className="text-xs font-bold uppercase tracking-wider text-neutral-600 dark:text-neutral-300 flex items-center gap-2">
            <Layers className="h-4 w-4 text-neutral-500" />
            {isEn ? "1. Task Profile & Primary Modality" : "1. Aufgabenprofil & Primäre Modalität"}
          </label>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {[
              {
                id: "json_extraction" as TaskCategory,
                label: isEn ? "JSON & Extraction" : "JSON & Extraktion",
                sub: isEn ? "Invoices, entities, schema" : "Belege, Entitäten, Klassifikation",
                icon: FileText,
                color: "text-blue-500",
              },
              {
                id: "reasoning_code" as TaskCategory,
                label: isEn ? "Reasoning & Code" : "Reasoning & Code",
                sub: isEn ? "Architecture, AST, logic" : "Architektur, AST, Logikbeweise",
                icon: Code2,
                color: "text-purple-500",
              },
              {
                id: "tabular_scoring" as TaskCategory,
                label: isEn ? "Tabular & Scoring" : "Tabellen & Scoring",
                sub: isEn ? "Fraud, churn, risk, math" : "Fraud, Churn, Risiko, Zahlen",
                icon: Database,
                color: "text-emerald-500",
              },
              {
                id: "multimodal_live" as TaskCategory,
                label: isEn ? "Multimodal & Audio" : "Multimodal & Audio",
                sub: isEn ? "Real-time speech & vision" : "Echtzeit-Sprache, Video-Stream",
                icon: Radio,
                color: "text-rose-500",
              },
              {
                id: "local_edge" as TaskCategory,
                label: isEn ? "On-Device / WebNN" : "On-Device / WebNN",
                sub: isEn ? "Browser offline inference" : "Offline-Inferenz im Browser",
                icon: Cpu,
                color: "text-amber-500",
              },
            ].map((item) => {
              const Icon = item.icon;
              const isSelected = taskType === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setTaskType(item.id)}
                  className={`flex min-h-[52px] items-center gap-3 rounded-xl border p-3 text-left transition-all cursor-pointer ${
                    isSelected
                      ? "border-neutral-900 bg-neutral-900 text-white shadow-md dark:border-white dark:bg-white dark:text-neutral-900"
                      : "border-neutral-200 bg-white hover:border-neutral-300 hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900 dark:hover:border-neutral-700"
                  }`}
                >
                  <Icon
                    className={`h-5 w-5 shrink-0 ${
                      isSelected ? "text-white dark:text-neutral-900" : item.color
                    }`}
                  />
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs font-bold leading-tight truncate">
                      {item.label}
                    </span>
                    <span
                      className={`text-[10px] leading-tight truncate ${
                        isSelected
                          ? "text-neutral-300 dark:text-neutral-600"
                          : "text-neutral-500 dark:text-neutral-400"
                      }`}
                    >
                      {item.sub}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right Column: Volume & Latency Sliders (5 cols) */}
        <div className="flex flex-col justify-between gap-5 border-t border-neutral-200/80 pt-5 dark:border-neutral-800/80 lg:border-t-0 lg:border-l lg:pl-6 lg:pt-0 lg:col-span-5">
          {/* Latency Preference */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-neutral-600 dark:text-neutral-300 flex items-center justify-between mb-2">
              <span className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 text-neutral-500" />
                {isEn ? "2. Latency Budget" : "2. Latenz-Budget"}
              </span>
              <span className="font-mono text-[11px] text-neutral-500">
                {latencyBudget === "ultra_low"
                  ? "< 50ms"
                  : latencyBudget === "interactive"
                  ? "< 800ms"
                  : "5s - 60s"}
              </span>
            </label>
            <div className="grid grid-cols-3 gap-1.5">
              {[
                { id: "ultra_low" as LatencyPreference, label: isEn ? "Real-Time" : "Echtzeit" },
                { id: "interactive" as LatencyPreference, label: isEn ? "Interactive" : "Interaktiv" },
                { id: "async_batch" as LatencyPreference, label: isEn ? "Batch" : "Batch" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setLatencyBudget(opt.id)}
                  className={`min-h-[44px] rounded-lg border py-2 px-2 text-center text-xs font-semibold transition-all cursor-pointer ${
                    latencyBudget === opt.id
                      ? "border-neutral-900 bg-neutral-900 text-white dark:border-white dark:bg-white dark:text-neutral-900"
                      : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Monthly Volume Slider */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-600 dark:text-neutral-300 flex items-center gap-1.5">
                <SlidersHorizontal className="h-3.5 w-3.5 text-neutral-500" />
                {isEn ? "3. Monthly Requests" : "3. Monatliche Requests"}
              </label>
              <span className="font-mono text-xs font-bold text-purple-600 dark:text-purple-400">
                {monthlyRequests.toLocaleString(numberLocale)} Req/Mo
              </span>
            </div>
            <input
              type="range"
              min={5000}
              max={1000000}
              step={5000}
              value={monthlyRequests}
              onChange={(e) => setMonthlyRequests(Number(e.target.value))}
              className="h-2 w-full appearance-none rounded-lg bg-neutral-200 accent-purple-600 dark:bg-neutral-800 cursor-pointer"
            />
            <div className="mt-1 flex justify-between text-[10px] text-neutral-400">
              <span>5k ({isEn ? "Prototype" : "Prototyp"})</span>
              <span>250k</span>
              <span>1M ({isEn ? "Scale" : "Scale"})</span>
            </div>
          </div>

          {/* Token Context Payload */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-neutral-600 dark:text-neutral-300 flex items-center justify-between mb-1.5">
              <span>{isEn ? "4. Context Payload" : "4. Kontext-Größe"}</span>
            </label>
            <div className="grid grid-cols-3 gap-1.5">
              {[
                { id: "compact" as TokenSizeOption, label: isEn ? "Compact" : "Kompakt" },
                { id: "standard" as TokenSizeOption, label: isEn ? "Mid (1.6k)" : "Mittel (1.6k)" },
                { id: "large" as TokenSizeOption, label: isEn ? "Large (8.5k)" : "Groß (8.5k)" },
              ].map((tok) => (
                <button
                  key={tok.id}
                  type="button"
                  onClick={() => setTokenSize(tok.id)}
                  className={`min-h-[44px] rounded-lg border py-1.5 px-2 text-center text-xs font-medium transition-all cursor-pointer ${
                    tokenSize === tok.id
                      ? "border-neutral-900 bg-neutral-900 text-white dark:border-white dark:bg-white dark:text-neutral-900"
                      : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300"
                  }`}
                >
                  {tok.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Dynamic Results Dashboard: Side-by-Side Comparison */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Naive / Traditional Path Card */}
        <div className="flex flex-col justify-between rounded-2xl border border-rose-500/25 bg-rose-50/30 p-5 dark:border-rose-500/20 dark:bg-rose-950/10">
          <div>
            <div className="flex items-center justify-between mb-3 border-b border-rose-200/60 pb-3 dark:border-rose-900/50">
              <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-rose-500/10 text-rose-600 dark:text-rose-400">
                  <AlertTriangle className="h-4 w-4" />
                </span>
                <span className="text-xs font-bold uppercase tracking-wider text-rose-800 dark:text-rose-300">
                  {isEn ? "Naive Baseline (Anti-Pattern)" : "Nativer / Naiver Ansatz (Anti-Pattern)"}
                </span>
              </div>
              <span className="rounded bg-rose-100 px-2 py-0.5 font-mono text-[10px] font-bold text-rose-800 dark:bg-rose-900/60 dark:text-rose-200">
                Frontier LLM Monolith
              </span>
            </div>

            <p className="text-xs text-neutral-600 dark:text-neutral-400 mb-4">
              {isEn
                ? "Typical mistake: Blindly piping all tasks to the same expensive generalist model (e.g. GPT-6 Sol / Claude Opus)."
                : "Typischer Fehler: Für alle Anfragen blind dasselbe teure Alleskönner-Modell (z. B. GPT-6 Sol / Claude Opus) einbinden."}
            </p>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="rounded-xl border border-rose-200/80 bg-white/80 p-3 dark:border-rose-900/40 dark:bg-neutral-900/80">
                <span className="text-[10px] uppercase font-bold text-neutral-500">
                  {isEn ? "Monthly API Cost" : "Monatliche API-Kosten"}
                </span>
                <div className="mt-1 font-mono text-xl font-black text-rose-600 dark:text-rose-400">
                  {isEn ? "$" : ""}
                  {calculations.naiveCost.toLocaleString(numberLocale, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                  {!isEn ? " $" : ""}
                </div>
              </div>

              <div className="rounded-xl border border-rose-200/80 bg-white/80 p-3 dark:border-rose-900/40 dark:bg-neutral-900/80">
                <span className="text-[10px] uppercase font-bold text-neutral-500">
                  {isEn ? "Inference Latency" : "Typische Inferenz-Latenz"}
                </span>
                <div className="mt-1 font-mono text-xl font-black text-neutral-800 dark:text-neutral-200">
                  ~{calculations.naiveLatencyMs.toLocaleString(numberLocale)} ms
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-white/70 p-2.5 text-xs text-rose-700 dark:border-rose-900/60 dark:bg-neutral-900/60 dark:text-rose-300">
              <AlertTriangle className="h-4 w-4 shrink-0 text-rose-500" />
              <span>
                {isEn ? "Hallucination Risk: " : "Halluzinations-Risiko: "}
                <strong>{calculations.naiveHallucinationRisk}</strong>
              </span>
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-rose-200/60 text-[11px] text-neutral-500 dark:border-rose-900/40">
            {isEn
              ? "⚠️ Causes runaway billing at scale and sluggish UI loading spinners."
              : "⚠️ Führt bei Skalierung zu explodierenden Rechnungen und trägem UI-Streaming."}
          </div>
        </div>

        {/* Optimized Best-Practice Stack Card */}
        <div className="flex flex-col justify-between rounded-2xl border border-emerald-500/35 bg-emerald-50/40 p-5 shadow-sm dark:border-emerald-500/30 dark:bg-emerald-950/20">
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3 border-b border-emerald-200/80 pb-3 dark:border-emerald-800/60">
              <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 className="h-4 w-4" />
                </span>
                <span className="text-xs font-bold uppercase tracking-wider text-emerald-900 dark:text-emerald-300">
                  {isEn ? "Recommended Compound Stack" : "Empfohlener Best-Practice-Stack"}
                </span>
              </div>
              <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 font-mono text-[10px] font-bold text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200">
                {calculations.optimizedBadge}
              </span>
            </div>

            <h4 className="font-heading text-sm sm:text-base font-bold text-neutral-900 dark:text-white mb-1">
              {calculations.optimizedStackName}
            </h4>
            <p className="text-xs text-neutral-600 dark:text-neutral-400 mb-4">
              {calculations.architecturalInsight}
            </p>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="rounded-xl border border-emerald-300/80 bg-white/95 p-3 dark:border-emerald-800/60 dark:bg-neutral-900/90">
                <span className="text-[10px] uppercase font-bold text-neutral-500">
                  {isEn ? "Optimized Cost / Mo" : "Optimierte Kosten / Mo"}
                </span>
                <div className="mt-1 font-mono text-xl font-black text-emerald-600 dark:text-emerald-400 flex items-baseline gap-1.5">
                  <span>
                    {isEn ? "$" : ""}
                    {calculations.optimizedCost.toLocaleString(numberLocale, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                    {!isEn ? " $" : ""}
                  </span>
                  <span className="text-xs font-bold text-emerald-700 dark:text-emerald-300">
                    (-{calculations.savingsPercent}%)
                  </span>
                </div>
              </div>

              <div className="rounded-xl border border-emerald-300/80 bg-white/95 p-3 dark:border-emerald-800/60 dark:bg-neutral-900/90">
                <span className="text-[10px] uppercase font-bold text-neutral-500">
                  {isEn ? "Real Latency" : "Reale Latenz"}
                </span>
                <div className="mt-1 font-mono text-xl font-black text-neutral-900 dark:text-white flex items-baseline gap-1.5">
                  <span>~{calculations.optimizedLatencyMs.toLocaleString(numberLocale)} ms</span>
                  <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">
                    ({calculations.speedupFactor}x {isEn ? "faster" : "schneller"})
                  </span>
                </div>
              </div>
            </div>

            {/* Savings Callout */}
            <div className="flex items-center justify-between rounded-xl border border-emerald-300 bg-white/90 p-3 text-xs text-emerald-900 dark:border-emerald-700/60 dark:bg-neutral-900/90 dark:text-emerald-200">
              <span className="flex items-center gap-1.5 font-bold">
                <TrendingDown className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                {isEn ? "Monthly Savings:" : "Monatliche Ersparnis:"}
              </span>
              <span className="font-mono text-sm font-black text-emerald-700 dark:text-emerald-300">
                + {isEn ? "$" : ""}
                {calculations.savingsDollars.toLocaleString(numberLocale, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                {isEn ? " / mo" : " $ / Monat"}
              </span>
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-emerald-200/80 text-[11px] text-emerald-800 dark:border-emerald-800/50 dark:text-emerald-300">
            {isEn
              ? "✓ Superior UX through sub-second responsiveness, deterministic verification, and controlled operational expense."
              : "✓ Exzellente UX durch niedrige Latenz, deterministische Validierung und planbare Betriebskosten."}
          </div>
        </div>
      </div>

      {/* Pipeline Visual Flow */}
      <div className="rounded-2xl border border-neutral-200/90 bg-white/80 p-5 dark:border-neutral-800 dark:bg-neutral-900/50">
        <div className="mb-3 flex items-center justify-between">
          <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-neutral-600 dark:text-neutral-300">
            <Gauge className="h-4 w-4 text-purple-500" />
            {isEn ? "Recommended Component Pipeline" : "Empfohlene Komponenten-Pipeline"}
          </span>
          <span className="text-xs text-neutral-400">
            {isEn ? "End-to-End Dataflow" : "End-to-End Datenfluss"}
          </span>
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {calculations.pipelineSteps.map((step, idx) => (
            <div
              key={idx}
              className="flex items-center gap-2 rounded-xl border border-neutral-200/70 bg-neutral-50/80 p-3 text-xs font-medium text-neutral-800 dark:border-neutral-800 dark:bg-neutral-850/60 dark:text-neutral-200"
            >
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-purple-500/10 font-mono text-[10px] font-bold text-purple-600 dark:text-purple-400">
                {idx + 1}
              </span>
              <span className="truncate">{step.replace(/^\d+\.\s*/, "")}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Copyable Code Blueprint */}
      <div className="overflow-hidden rounded-2xl border border-neutral-800/90 bg-zinc-950 shadow-xl">
        {/* Code Header Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-800/80 bg-zinc-900/90 px-4 py-2.5">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-red-500/80" />
              <span className="h-2.5 w-2.5 rounded-full bg-amber-500/80" />
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/80" />
            </div>

            {/* Code Tabs */}
            <div className="flex items-center gap-1 ml-2">
              {[
                { id: "router" as const, label: isEn ? "Router / Handler" : "Router / Handler" },
                { id: "schema" as const, label: isEn ? "Schema / Config" : "Schema / Config" },
                { id: "benchmark" as const, label: isEn ? "Benchmark Details" : "Benchmark-Vergleich" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveCodeTab(tab.id)}
                  className={`min-h-[36px] rounded-lg px-2.5 py-1 text-xs font-mono font-medium transition-all cursor-pointer ${
                    activeCodeTab === tab.id
                      ? "bg-zinc-800 text-white shadow-xs"
                      : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={handleCopyCode}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1 font-mono text-xs font-semibold transition-all cursor-pointer ${
              copiedCode
                ? "bg-emerald-950 text-emerald-400 border border-emerald-700/60"
                : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700 hover:text-white"
            }`}
          >
            {copiedCode ? (
              <>
                <Check className="h-3.5 w-3.5 text-emerald-400" />
                <span>{isEn ? "Copied!" : "Kopiert!"}</span>
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5 opacity-70" />
                <span>{isEn ? "Copy code" : "Code kopieren"}</span>
              </>
            )}
          </button>
        </div>

        {/* Code Body */}
        <div className="p-4 overflow-x-auto text-xs sm:text-sm font-mono">
          <CodeHighlight
            code={calculations.codeSnippets[activeCodeTab]}
            language={activeCodeTab === "benchmark" ? "bash" : "typescript"}
          />
        </div>
      </div>
    </div>
  );
}
